import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelectedColoc } from '../hooks/useSelectedColoc';
import { tenantsService } from '../services/tenants.service';
import {
  GUARANTOR_TYPE_OPTIONS,
  TenantDetail,
  TenantDocument,
  RoomSummary,
  getTenantStatus,
  getTenantStatusLabel,
  etatAcces,
  libelleAcces,
  TENANT_DOCUMENT_TYPE_LABELS,
  UPLOADABLE_DOCUMENT_TYPES,
} from '../types/tenant';
import { formatCurrency, formatDate } from '../utils/format';
import MentionBail from '../components/MentionBail';
import '../styles/TenantsPage.css';
import '../styles/DetailPages.css';
import { getErrorMessage } from '../utils/error';

const DOCUMENT_TYPE_LABELS = TENANT_DOCUMENT_TYPE_LABELS;
const DOCUMENT_TYPES = UPLOADABLE_DOCUMENT_TYPES;

/**
 * Le dossier d'un locataire — une PAGE, plus un panneau latéral.
 *
 * Quatre sections (location, dates, accès, documents) et cinq actions de cycle
 * de vie (réserver, préavis, annuler préavis, état des lieux, sortie) tenaient
 * dans 460 px, au prix de la moitié des colonnes de la liste. Dernier écran du
 * back-office à porter ce motif avec Chambres.
 *
 * **Les actions vivent ici**, pas dans la liste : donner congé ou faire sortir
 * quelqu'un ne se décide pas depuis une cellule de tableau. Même partage que
 * Candidats, Contacts et Agences.
 */
const TenantDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const selectedColoc = useSelectedColoc();

  const [tenants, setTenants] = useState<TenantDetail[]>([]);
  const [rooms, setRooms] = useState<RoomSummary[]>([]);
  const [introuvable, setIntrouvable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Réservation d'une chambre
  const [checkInUserId, setCheckInUserId] = useState<string | null>(null);
  const [checkInRoomId, setCheckInRoomId] = useState('');
  const [checkInStartDate, setCheckInStartDate] = useState('');
  const [checkInRent, setCheckInRent] = useState('');
  const [checkInCharges, setCheckInCharges] = useState('');
  // Jamais demandés jusqu'au 2026-08-31 : l'API les acceptait et `TenantInfo`
  // les stockait, mais le formulaire ne les proposait pas — ils restaient donc
  // vides pour tout locataire réservé depuis cet écran.
  const [checkInDeposit, setCheckInDeposit] = useState('');
  const [checkInAgencyFees, setCheckInAgencyFees] = useState('');
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [checkInError, setCheckInError] = useState<string | null>(null);

  // Préavis
  const [noticeId, setNoticeId] = useState<string | null>(null);
  const [noticeDate, setNoticeDate] = useState('');
  const [noticeExitDate, setNoticeExitDate] = useState('');
  const [noticeLoading, setNoticeLoading] = useState(false);
  const [noticeError, setNoticeError] = useState<string | null>(null);
  const [cancelNoticeId, setCancelNoticeId] = useState<string | null>(null);

  // État des lieux d'entrée
  const [entryInvId, setEntryInvId] = useState<string | null>(null);
  const [entryInvDate, setEntryInvDate] = useState('');
  const [entryInvLoading, setEntryInvLoading] = useState(false);
  const [entryInvError, setEntryInvError] = useState<string | null>(null);

  // Sortie
  const [checkOutId, setCheckOutId] = useState<string | null>(null);
  const [checkOutDate, setCheckOutDate] = useState('');
  const [checkOutInventoryDate, setCheckOutInventoryDate] = useState('');
  const [checkOutLoading, setCheckOutLoading] = useState(false);
  const [checkOutError, setCheckOutError] = useState<string | null>(null);

  // Documents
  const [uploadTenantId, setUploadTenantId] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState('LEASE');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);

  const [accessLoadingId, setAccessLoadingId] = useState<string | null>(null);

  // Correction des conditions du bail. Ces montants et cette date n'étaient
  // écrits qu'à la création du dossier et jamais réaffectés : une faute de
  // frappe était définitive, sauf à passer par la base.
  const [editionBail, setEditionBail] = useState(false);
  const [bailPrenom, setBailPrenom] = useState('');
  const [bailNom, setBailNom] = useState('');
  const [bailStart, setBailStart] = useState('');
  const [bailRent, setBailRent] = useState('');
  const [bailCharges, setBailCharges] = useState('');
  const [bailDeposit, setBailDeposit] = useState('');
  const [bailAgencyFees, setBailAgencyFees] = useState('');
  const [bailGuarantor, setBailGuarantor] = useState('');
  const [bailBusy, setBailBusy] = useState(false);
  const [renvoiEnCours, setRenvoiEnCours] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    // Pas encore de colocation sélectionnée : on RESTE en chargement. Sur un
    // lien direct, `Layout` la pose juste après le montage et relance ceci.
    if (!selectedColoc?.id || !id) return;
    setLoading(true);
    try {
      const [tenantsData, roomsData] = await Promise.all([
        tenantsService.listForColoc(selectedColoc.id),
        tenantsService.listRooms(selectedColoc.id),
      ]);
      setTenants(tenantsData);
      setRooms(roomsData);
      // Même repli que la résolution du dossier plus bas : un locataire sans
      // bail est listé sous l'identifiant de son UTILISATEUR, et l'URL le garde
      // après la réservation. Chercher sur le seul `id` déclarait introuvable un
      // dossier que la page savait pourtant afficher.
      setIntrouvable(!tenantsData.some((t) => t.id === id || t.userId === id));
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du chargement du locataire'));
    } finally {
      setLoading(false);
    }
  }, [selectedColoc, id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Le dossier ouvert, relu dans la liste à jour : figer l'objet afficherait un
   * statut périmé après un préavis ou une sortie.
   *
   * Le repli sur `userId` n'est pas décoratif. Un locataire SANS bail est listé
   * sous l'identifiant de son UTILISATEUR, faute de `TenantInfo`
   * (`_user_to_detail_sans_bail` côté API). Lui réserver une chambre crée un
   * vrai dossier, avec un identifiant NEUF — l'URL ouverte ne désignait alors
   * plus rien et la page annonçait « Locataire introuvable » alors que la liste
   * l'affichait très bien.
   */
  const selection = tenants.find((t) => t.id === id)
    || tenants.find((t) => t.userId === id)
    || null;  const handleCheckIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkInUserId) return;
    setCheckInLoading(true);
    setCheckInError(null);
    try {
      const dossier = await tenantsService.checkIn(checkInUserId, {
        room_id: checkInRoomId,
        lease_start_date: new Date(checkInStartDate).toISOString(),
        monthly_rent: parseFloat(checkInRent),
        charges_provision: checkInCharges ? parseFloat(checkInCharges) : null,
        deposit_amount: checkInDeposit ? parseFloat(checkInDeposit) : null,
        agency_expenses: checkInAgencyFees ? parseFloat(checkInAgencyFees) : null,
      });
      setCheckInUserId(null);
      // L'URL portait l'identifiant de l'utilisateur ; elle doit désormais
      // porter celui du dossier. Le repli ci-dessus suffirait à afficher la
      // page, mais laisserait une adresse qui ne désigne pas ce qu'elle montre.
      if (dossier?.id && dossier.id !== id) {
        navigate(`/tenants/${dossier.id}`, { replace: true });
        return;
      }
      await loadData();
    } catch (err) {
      setCheckInError(getErrorMessage(err, "Erreur lors de l'entrée"));
    } finally {
      setCheckInLoading(false);
    }
  };

  const handleGiveNotice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noticeId) return;
    setNoticeLoading(true);
    setNoticeError(null);
    try {
      await tenantsService.giveNotice(noticeId, {
        notice_date: new Date(noticeDate).toISOString(),
        expected_exit_date: new Date(noticeExitDate).toISOString(),
      });
      setNoticeId(null);
      await loadData();
    } catch (err) {
      setNoticeError(getErrorMessage(err, 'Erreur lors du préavis'));
    } finally {
      setNoticeLoading(false);
    }
  };

  const handleCancelNotice = async (tenantInfoId: string) => {
    try {
      await tenantsService.cancelNotice(tenantInfoId);
      setCancelNoticeId(null);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, "Erreur lors de l'annulation du préavis"));
    }
  };

  /** Une réservation faite par erreur se défait tant que le bail n'a pas
   *  commencé (#17). Née d'une candidature, le serveur refuse : c'est son bail
   *  qu'on annule, depuis la fiche du candidat. */
  const handleCancelReservation = async (tenantInfoId: string) => {
    if (!window.confirm(
      'Annuler cette réservation ? La chambre redevient libre ; le compte du locataire est supprimé s\'il n\'a pas d\'autre dossier.',
    )) return;
    try {
      await tenantsService.cancelReservation(tenantInfoId);
      navigate('/tenants');
    } catch (err) {
      setError(getErrorMessage(err, "Erreur lors de l'annulation de la réservation"));
    }
  };

  /**
   * Date d'état des lieux d'ENTRÉE. Avec le bail téléversé, c'est ce qui ouvre
   * l'accès du locataire à son espace. Les deux se saisissent séparément parce
   * qu'ils n'arrivent presque jamais ensemble : l'état des lieux se fait à la
   * remise des clés, le bail signé revient de l'agence quand il revient.
   */
  const handleEntryInventory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!entryInvId) return;
    setEntryInvLoading(true);
    setEntryInvError(null);
    try {
      await tenantsService.setEntryInventory(entryInvId, new Date(entryInvDate).toISOString());
      setEntryInvId(null);
      setEntryInvDate('');
      await loadData();
    } catch (err) {
      setEntryInvError(getErrorMessage(err, "Erreur lors de l'enregistrement"));
    } finally {
      setEntryInvLoading(false);
    }
  };

  const handleCheckOut = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkOutId) return;
    setCheckOutLoading(true);
    setCheckOutError(null);
    try {
      await tenantsService.checkOut(checkOutId, {
        lease_end_date: new Date(checkOutDate).toISOString(),
        exit_inventory_date: new Date(checkOutInventoryDate).toISOString(),
      });
      setCheckOutId(null);
      await loadData();
    } catch (err) {
      setCheckOutError(getErrorMessage(err, 'Erreur lors de la sortie'));
    } finally {
      setCheckOutLoading(false);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadTenantId || !uploadFile) return;
    setUploadLoading(true);
    setUploadError(null);
    try {
      await tenantsService.uploadDocument(uploadTenantId, uploadFile, uploadDocType, uploadNotes || undefined);
      setUploadTenantId(null);
      setUploadFile(null);
      setUploadNotes('');
      await loadData();
    } catch (err) {
      setUploadError(getErrorMessage(err, "Erreur lors de l'envoi"));
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDownloadDoc = async (tenantInfoId: string, doc: TenantDocument) => {
    try {
      const blob = await tenantsService.downloadDocument(tenantInfoId, doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du téléchargement'));
    }
  };

  /**
   * Retire une pièce du dossier. Jusqu'au 2026-08-30 il n'existait AUCUNE voie
   * de suppression : une pièce mal classée ou erronée restait là pour toujours.
   *
   * Le PDF d'un bail SIGNÉ fait exception et le backend répond 409 — il partage
   * son stockage avec la fiche de bail, l'effacer laisserait le contrat
   * introuvable. Le message du backend est affiché tel quel.
   */
  const handleDeleteDoc = async (tenantInfoId: string, doc: TenantDocument) => {
    if (!window.confirm(
      `Supprimer « ${doc.fileName} » ? Le fichier est effacé définitivement.`,
    )) return;
    setDeletingDocId(doc.id);
    setError(null);
    try {
      await tenantsService.deleteDocument(tenantInfoId, doc.id);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors de la suppression du document'));
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleToggleAccess = async (tenant: TenantDetail) => {
    setAccessLoadingId(tenant.id);
    setError(null);
    try {
      await tenantsService.setAccess(tenant.id, !tenant.accessEnabled);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, "Erreur lors de la modification de l'accès"));
    } finally {
      setAccessLoadingId(null);
    }
  };

  // Chambres déjà prises : réservées ou occupées. Pas « un dossier actif » : un
  // locataire parti garde le sien six mois, sa chambre est pourtant libre.
  // Déduit de la liste déjà chargée — aucun appel de plus.

  const chambresPrises = new Set(
    tenants.filter(t => t.occupeLaChambre && t.roomId).map(t => t.roomId as string),
  );
  // Proposables : ni prises, ni fermées par le bailleur (le serveur refuserait).
  const proposables = rooms.filter(r => !r.isClosed && !chambresPrises.has(r.id));

  /**
   * Choisit la chambre ET reprend ses montants.
   *
   * Le loyer et les charges de la chambre sont la valeur attendue neuf fois
   * sur dix ; les resaisir à la main invitait à la faute de frappe, sur des
   * montants qui suivent le locataire pendant tout son bail. Même geste que la
   * modale d'acceptation d'un candidat, qui préremplit depuis la chambre.
   *
   * Écrase ce qui était saisi : changer de chambre change les montants, sans
   * quoi on garderait ceux de la précédente sans s'en apercevoir.
   */
  const appliquerChambre = (roomId: string) => {
    setCheckInRoomId(roomId);
    const chambre = rooms.find((r) => r.id === roomId);
    setCheckInRent(chambre?.priceNIC != null ? String(chambre.priceNIC) : '');
    setCheckInCharges(chambre?.charges != null ? String(chambre.charges) : '');
    setCheckInDeposit(chambre?.depositAmount != null ? String(chambre.depositAmount) : '');
    setCheckInAgencyFees(chambre?.agencyExpenses != null ? String(chambre.agencyExpenses) : '');
  };

  const openCheckIn = (userId: string) => {
    setCheckInUserId(userId);
    // La première chambre LIBRE, pas la première tout court : le menu ne
    // propose que les libres, et présélectionner une chambre prise donnait un
    // `value` sans option correspondante — le menu affichait alors autre chose
    // que ce que l'état croyait sélectionné.
    appliquerChambre(proposables[0]?.id ?? '');
    setCheckInStartDate('');
    setCheckInError(null);
  };

  const openNotice = (tenantInfoId: string) => {
    setNoticeId(tenantInfoId);
    setNoticeDate('');
    setNoticeExitDate('');
    setNoticeError(null);
  };

  /** Préremplie avec les dates déjà saisies : c'est ce qui sert « Corriger la
   *  sortie ». Vides pour une première sortie. */
  const openCheckOut = (tenant: TenantDetail) => {
    setCheckOutId(tenant.id);
    setCheckOutDate(tenant.leaseEndDate?.split('T')[0] ?? '');
    setCheckOutInventoryDate(tenant.exitInventoryDate?.split('T')[0] ?? '');
    setCheckOutError(null);
  };

  const openUpload = (tenantInfoId: string, type = 'LEASE') => {
    setUploadTenantId(tenantInfoId);
    setUploadFile(null);
    setUploadDocType(type);
    setUploadNotes('');
    setUploadError(null);
  };

  // --- Status badge ---

  const renderStatusBadge = (tenant: TenantDetail) => {
    const status = getTenantStatus(tenant);
    const label = getTenantStatusLabel(status);
    return <span className={`tenant-status-badge status-${status}`}>{label}</span>;
  };

  // --- Render ---

  if (loading) {
    return <div className="tenants-loading">Chargement...</div>;
  }

  if (!selectedColoc) {
    return <div className="tenants-empty">Sélectionnez une colocation</div>;
  }

  // La ligne ouverte, relue dans la liste à jour : garder l'objet en état
  // afficherait des valeurs périmées après un préavis ou un téléversement.

  /**
   * Renvoie au locataire son lien de création de mot de passe.
   *
   * Proposé EN PERMANENCE, pas seulement avant la première connexion : un
   * locataire installé depuis six mois peut perdre son accès, et c'est aussi
   * le chemin de réinitialisation côté propriétaire. Le libellé le distingue
   * de l'interrupteur d'accès voisin, qui ne parle pas de la même chose.
   */
  const renvoyerLien = async () => {
    if (!selection) return;
    setRenvoiEnCours(true);
    setError(null);
    setNotice(null);
    try {
      const res = await tenantsService.resendWelcomeEmail(selection.id);
      setNotice(
        res.sent
          ? 'Lien envoyé. Il reste valable un temps limité, et les liens précédents fonctionnent toujours.'
          : "L'envoi n'a pas pu aboutir. Vérifiez l'adresse email du locataire.",
      );
    } catch (err) {
      setError(getErrorMessage(err, "Erreur lors de l'envoi du lien"));
    } finally {
      setRenvoiEnCours(false);
    }
  };

  const nombreOuVide = (v: number | null | undefined) => (v != null ? String(v) : '');

  const ouvrirEditionBail = () => {
    if (!selection) return;
    setBailPrenom(selection.firstName);
    setBailNom(selection.lastName);
    setBailStart(selection.leaseStartDate ? selection.leaseStartDate.slice(0, 10) : '');
    setBailRent(nombreOuVide(selection.monthlyRent));
    setBailCharges(nombreOuVide(selection.chargesProvision));
    setBailDeposit(nombreOuVide(selection.depositAmount));
    setBailAgencyFees(nombreOuVide(selection.agencyExpenses));
    setBailGuarantor(selection.guarantorType || '');
    setError(null);
    setNotice(null);
    setEditionBail(true);
  };

  /** Correction de saisie : on n'envoie que ce qui est renseigné. */
  const enregistrerBail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selection) return;
    setBailBusy(true);
    setError(null);
    try {
      await tenantsService.updateLeaseTerms(selection.id, {
        first_name: bailPrenom,
        last_name: bailNom,
        ...(bailStart ? { lease_start_date: new Date(bailStart).toISOString() } : {}),
        ...(bailRent ? { monthly_rent: parseFloat(bailRent) } : {}),
        ...(bailCharges ? { charges_provision: parseFloat(bailCharges) } : {}),
        ...(bailDeposit ? { deposit_amount: parseFloat(bailDeposit) } : {}),
        ...(bailAgencyFees ? { agency_expenses: parseFloat(bailAgencyFees) } : {}),
        // Toujours envoyé, contrairement aux montants : « non renseigné » est
        // une valeur qu'on doit pouvoir REMETTRE, pas seulement quitter.
        guarantor_type: bailGuarantor || null,
      });
      setEditionBail(false);
      setNotice('Fiche corrigée.');
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors de la correction'));
    } finally {
      setBailBusy(false);
    }
  };

  if (loading) return <div className="loading">Chargement...</div>;

  if (introuvable || !selection) {
    return (
      <div className="tenants-page">
        <div className="socle-vide">
          <p className="socle-vide-titre">Locataire introuvable</p>
          <p className="socle-vide-aide">
            Ce dossier a été supprimé, ou il appartient à une autre colocation
            que celle sélectionnée.
          </p>
          <button className="btn-action btn-secondary" onClick={() => navigate('/tenants')}>
            Retour aux locataires
          </button>
        </div>
      </div>
    );
  }

  const statut = getTenantStatus(selection);

  return (
    <div className="tenants-page">
      <div className="tenants-header">
        <div>
          <button
            className="btn-action btn-secondary"
            style={{ marginBottom: 8 }}
            onClick={() => navigate('/tenants')}
          >
            ← Locataires
          </button>
          <h2>
            {selection.firstName} {selection.lastName}
            <span style={{ marginLeft: 12, verticalAlign: 'middle' }}>
              {renderStatusBadge(selection)}
            </span>
          </h2>
          <p className="access-hint" style={{ margin: 0 }}>
            {selection.roomName || 'Aucune chambre assignée'}
          </p>
        </div>

        {/* Le cycle de vie se pilote ici, dossier ouvert. Les conditions sont
            celles qui vivaient dans la cellule d'actions du tableau. */}
        <div className="tenants-header-actions">
          {!selection.roomId && selection.isActive !== true && (
            <button className="btn-action btn-checkin" onClick={() => openCheckIn(selection.userId)}>
              Réserver une chambre
            </button>
          )}
          {statut === 'active' && (
            <button className="btn-action btn-notice" onClick={() => openNotice(selection.id)}>
              Préavis
            </button>
          )}
          {statut === 'notice' && (
            cancelNoticeId === selection.id ? (
              <span className="cancel-notice-confirm">
                <button className="btn-action btn-danger" onClick={() => handleCancelNotice(selection.id)}>
                  Confirmer
                </button>
                <button className="btn-action btn-secondary" onClick={() => setCancelNoticeId(null)}>
                  Non
                </button>
              </span>
            ) : (
              <button className="btn-action btn-warning" onClick={() => setCancelNoticeId(selection.id)}>
                Annuler préavis
              </button>
            )
          )}
          {/* Une date du dossier, plus une condition d'accès. Proposée tant
              qu'elle manque — c'est une pièce que la complétude réclame. */}
          {selection.isActive && !selection.entryInventoryDate && (
            <button className="btn-action btn-checkin" onClick={() => setEntryInvId(selection.id)}>
              État des lieux d&apos;entrée
            </button>
          )}
          {/* Le bail signé ne bloque plus l'accès : il est devenu une pièce
              du dossier, réclamée par la complétude. La mention « En attente du
              bail signé » qui vivait ici n'a plus d'objet. */}
          {/* Sortie : il occupe la chambre. Couper son accès à l'espace ne
              change rien à son bail, donc ne doit pas masquer ce bouton. */}
          {selection.occupeLaChambre && selection.roomId && statut !== 'reserved' && (
            <button className="btn-action btn-checkout" onClick={() => openCheckOut(selection)}>
              Sortie
            </button>
          )}
          {statut === 'reserved' && (
            <button className="btn-action btn-secondary" onClick={() => handleCancelReservation(selection.id)}>
              Annuler la réservation
            </button>
          )}
          {/* Une date d'état des lieux erronée se corrige par la même route,
              avec la même règle des 60 jours. `isActive` : un dossier terminé
              avant août n'a plus de sortie à corriger. */}
          {statut === 'exited' && selection.isActive && (
            <button className="btn-action btn-checkout" onClick={() => openCheckOut(selection)}>
              Corriger la sortie
            </button>
          )}
        </div>
      </div>

      {error && <div className="tenants-error">{error}</div>}
      {notice && <div className="candidats-notice">{notice}</div>}

      <div className="tenant-detail">
          <section className="panneau-section">
            <h3>
              Location
              {/* Ces montants n'étaient corrigeables nulle part : écrits à la
                  création du dossier, jamais réaffectés. Une faute de frappe
                  demandait une écriture en base, ou une sortie suivie d'une
                  nouvelle réservation qui laissait un dossier fantôme. */}
              {/* Sans chambre, pas de bail à corriger : cf. la section Accès. */}
              {!editionBail && selection.roomId && (
                <button className="btn-action btn-secondary" onClick={ouvrirEditionBail}>
                  Corriger
                </button>
              )}
            </h3>
            {editionBail ? (
              <form onSubmit={enregistrerBail} className="tenants-form">
                {/* Le nom n'était corrigeable nulle part depuis la fiche : une
                    faute à la création restait définitive (Julien, 2026-09-29). */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Prénom</label>
                    <input type="text" className="form-control" value={bailPrenom}
                           onChange={e => setBailPrenom(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Nom</label>
                    <input type="text" className="form-control" value={bailNom}
                           onChange={e => setBailNom(e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Début du bail</label>
                  <input type="date" className="form-control" value={bailStart}
                         onChange={e => setBailStart(e.target.value)} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Loyer mensuel (€)</label>
                    <input type="number" step="0.01" className="form-control" value={bailRent}
                           onChange={e => setBailRent(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Provision charges (€)</label>
                    <input type="number" step="0.01" className="form-control" value={bailCharges}
                           onChange={e => setBailCharges(e.target.value)} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Dépôt de garantie (€)</label>
                    <input type="number" step="0.01" className="form-control" value={bailDeposit}
                           onChange={e => setBailDeposit(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Frais d&apos;agence (€)</label>
                    <input type="number" step="0.01" className="form-control" value={bailAgencyFees}
                           onChange={e => setBailAgencyFees(e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Type de garantie</label>
                  <select className="form-control" value={bailGuarantor}
                          onChange={e => setBailGuarantor(e.target.value)}>
                    {GUARANTOR_TYPE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {/* Tant qu'il est vide, aucune pièce de garantie n'est
                      réclamée au dossier : l'indicateur se tait plutôt que de
                      mentir sur un dossier venu de l'agence. */}
                  <small className="form-hint">
                    Commande la pièce attendue au dossier. Laissé « non renseigné »,
                    aucune pièce de garantie n&apos;est réclamée.
                  </small>
                </div>
                <div className="cx-actions">
                  <button type="submit" className="btn-action btn-save" disabled={bailBusy}>
                    {bailBusy ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                  <button type="button" className="btn-action btn-secondary" disabled={bailBusy}
                          onClick={() => setEditionBail(false)}>
                    Annuler
                  </button>
                </div>
              </form>
            ) : (
              <dl className="socle-champs">
                <dt>Email</dt>
                <dd>{selection.email}</dd>
                <dt>Chambre</dt>
                <dd>{selection.roomName || 'Non assignée'}</dd>
                <dt>Loyer mensuel</dt>
                <dd>{selection.monthlyRent != null ? formatCurrency(selection.monthlyRent) : '—'}</dd>
                <dt>Provision charges</dt>
                <dd>{selection.chargesProvision != null ? formatCurrency(selection.chargesProvision) : '—'}</dd>
                <dt>Dépôt de garantie</dt>
                <dd>{selection.depositAmount != null ? formatCurrency(selection.depositAmount) : '—'}</dd>
                <dt>Frais d&apos;agence</dt>
                <dd>{selection.agencyExpenses != null ? formatCurrency(selection.agencyExpenses) : '—'}</dd>
              </dl>
            )}
          </section>

          <section className="panneau-section">
            <h3>Dates</h3>
            <dl className="socle-champs">
              <dt>Début du bail</dt>
              <dd>{selection.leaseStartDate ? formatDate(selection.leaseStartDate) : '—'}</dd>
              <dt>Fin du bail</dt>
              <dd>{selection.leaseEndDate ? formatDate(selection.leaseEndDate) : '—'}</dd>
              {/* Ces deux dates commandent l'accès et la libération de la
                  chambre : elles n'avaient leur place nulle part côté bailleur. */}
              <dt>État des lieux d'entrée</dt>
              <dd>{selection.entryInventoryDate ? formatDate(selection.entryInventoryDate) : '—'}</dd>
              <dt>État des lieux de sortie</dt>
              <dd>{selection.exitInventoryDate ? formatDate(selection.exitInventoryDate) : '—'}</dd>
              {getTenantStatus(selection) === 'notice' && (
                <>
                  <dt>Préavis déposé le</dt>
                  <dd>{selection.noticeDate ? formatDate(selection.noticeDate) : '—'}</dd>
                  <dt>Sortie prévisionnelle</dt>
                  <dd>{selection.noticeExpectedExitDate ? formatDate(selection.noticeExpectedExitDate) : '—'}</dd>
                </>
              )}
            </dl>
          </section>

          {/* Le dossier, en UN bloc : ce qui manque et ce qui est là.
              Ils étaient séparés — « Complétude » listait des pièces absentes,
              « Documents » plus bas les pièces présentes — si bien qu'on lisait
              « Bail signé » manquant sans voir, trois blocs plus loin, où le
              déposer. Chaque manque est désormais une ligne qui ouvre le dépôt
              sur le bon type.

              La complétude reste une liste de courses, jamais un blocage, et ne
              juge QUE les locataires dont le bail a commencé. */}
          <section className="panneau-section">
            <h3>
              Dossier
              {selection.dossierSuivi && (
                <span className={`tenant-access-badge access-${selection.dossierManques.length === 0 ? 'on' : 'off'}`}>
                  {selection.dossierManques.length === 0
                    ? 'Complet'
                    : `${selection.dossierManques.length} pièce${selection.dossierManques.length > 1 ? 's' : ''} manquante${selection.dossierManques.length > 1 ? 's' : ''}`}
                </span>
              )}
              {selection.roomId && (
                <button className="btn-action btn-upload" onClick={() => openUpload(selection.id)}>
                  Ajouter
                </button>
              )}
            </h3>
            {selection.dossierSuivi && selection.dossierManques.length > 0 && (
              <p className="access-hint">
                Attendues depuis l&apos;entrée dans les lieux. Rien n&apos;est
                bloqué : c&apos;est une liste de courses, et votre agence la
                voit aussi pour pouvoir déposer ce qui manque.
              </p>
            )}

            {(selection.dossierSuivi ? selection.dossierManques.length : 0) === 0
              && selection.documents.length === 0 ? (
              <p className="no-docs">Aucun document</p>
            ) : (
              <div className="docs-list">
                {selection.dossierSuivi && selection.dossierManques.map(m => (
                  <div key={m.code} className="doc-item doc-manquant">
                    <span className="doc-type-badge doc-badge-manquant">À déposer</span>
                    <span className="doc-filename doc-libelle-manquant">{m.label}</span>
                    {selection.roomId && (
                      <button
                        type="button"
                        className="btn-action btn-upload"
                        onClick={() => openUpload(selection.id, m.types[0])}
                      >
                        Déposer
                      </button>
                    )}
                  </div>
                ))}
                {selection.documents.map(doc => (
                  <div key={doc.id} className="doc-item">
                    <span className="doc-type-badge">
                      {/* Les pieces de candidature arrivent en lot, nommees par le
                          telephone du candidat (p_1787245227054.jpg) : sans le
                          « garant » porte par les notes, rien ne les distingue. */}
                      {doc.notes?.includes('garant')
                        ? 'Pièce du garant'
                        : DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType}
                    </span>
                    <button
                      type="button"
                      className="doc-filename"
                      onClick={() => handleDownloadDoc(selection.id, doc)}
                      title="Télécharger"
                    >
                      {doc.fileName}
                    </button>
                    <span className="doc-date">{formatDate(doc.uploadedAt)}</span>
                    <button
                      type="button"
                      className="btn-action btn-danger"
                      disabled={deletingDocId === doc.id}
                      onClick={() => handleDeleteDoc(selection.id, doc)}
                      title="Supprimer définitivement cette pièce"
                    >
                      {deletingDocId === doc.id ? '…' : 'Supprimer'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {selection.dossierSuivi && !selection.guarantorType && (
              <p className="access-hint" style={{ marginTop: 10 }}>
                Type de garantie non renseigné : aucune pièce de garantie
                n&apos;est réclamée. Précisez-le avec « Corriger » pour savoir
                laquelle attendre.
              </p>
            )}
            <MentionBail />
          </section>

          {/* `accessEnabled` n'est plus « il a emménagé » mais un VETO du
              propriétaire (2026-08-31). L'espace s'ouvre au début du bail ; ce
              bouton sert uniquement à couper, ou à rétablir ce qu'on a coupé. */}
          {/* Sans chambre, pas de dossier : la ligne porte l'identifiant du
              COMPTE, que ni l'accès ni le renvoi du lien ne connaissent. Seule
              « Réserver une chambre » a un sens ; la section revient avec la
              chambre, et c'est alors elle qui envoie le lien. */}
          {selection.roomId && (
            <section className="panneau-section">
              <h3>
                Accès à l'espace locataire
                <span className={`tenant-access-badge access-${etatAcces(selection) === 'ouvert' ? 'on' : 'off'}`}>
                  {libelleAcces(etatAcces(selection))}
                </span>
              </h3>
              <p className="access-hint">
                {!selection.accessEnabled
                  ? "Vous avez coupé son accès. Son bail et sa chambre sont inchangés."
                  : statut === 'reserved'
                    ? "L'espace s'ouvrira tout seul au début du bail. Ni le bail signé ni l'état des lieux ne sont nécessaires pour cela."
                    : etatAcces(selection) === 'ferme'
                      ? "Sa location est terminée : son espace s'est fermé six mois après l'état des lieux de sortie."
                      : 'Le locataire consulte son dossier et ses charges. Le couper ne modifie pas le bail.'}
              </p>
              <button
                className={`btn-action ${selection.accessEnabled ? 'btn-warning' : 'btn-save'}`}
                onClick={() => handleToggleAccess(selection)}
                disabled={accessLoadingId === selection.id}
              >
                {accessLoadingId === selection.id
                  ? 'Enregistrement...'
                  : selection.accessEnabled
                    ? "Couper l'accès"
                    : "Rétablir l'accès"}
              </button>
              <button
                className="btn-action btn-secondary"
                style={{ marginLeft: 8 }}
                onClick={() => navigate(`/tenants/${selection.id}/espace/dashboard`)}
              >
                Voir son espace
              </button>

              {/* Depuis que les formulaires de création ne prennent plus de mot de
                  passe, ce renvoi est la SEULE voie de rattrapage : un lien expiré
                  ou un email perdu laisse sinon le locataire dehors. La modale de
                  création le promet — « vous pourrez le lui renvoyer depuis sa
                  fiche » — et c'est ici. */}
              <p className="access-hint" style={{ marginTop: 14 }}>
                Le locataire choisit lui-même son mot de passe par un lien reçu par
                email. Vous pouvez le lui renvoyer : lien expiré, email perdu, ou
                mot de passe oublié. Les liens déjà envoyés restent valables.
              </p>
              <button
                className="btn-action btn-secondary"
                onClick={renvoyerLien}
                disabled={renvoiEnCours}
              >
                {renvoiEnCours ? 'Envoi...' : 'Renvoyer le lien de connexion'}
              </button>
            </section>
          )}

      </div>


      {/* Check-in modal */}
      {checkInUserId && (
        <div className="modal-overlay" onClick={() => setCheckInUserId(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Réserver une chambre</h3>
            {checkInError && <div className="tenants-error">{checkInError}</div>}
            {/* Sans cette phrase, le bailleur croit avoir installé son
                locataire et s'étonne que son espace reste fermé. */}
            {proposables.length === 0 && rooms.length > 0 && (
              <p className="tenants-error">
                Toutes les chambres de cette colocation sont occupées, réservées ou fermées.
              </p>
            )}
            <p className="tenants-modal-hint">
              La chambre passera en <strong>Réservée</strong> et ne recevra plus
              de candidatures. L&apos;accès du locataire à son espace
              s&apos;ouvrira <strong>au début du bail</strong>, sans autre
              condition : ni le bail signé ni l&apos;état des lieux d&apos;entrée
              ne le retardent.
            </p>
            <form onSubmit={handleCheckIn} className="tenants-form">
              <div className="form-group">
                <label>Chambre</label>
                <select className="form-control" value={checkInRoomId} onChange={e => appliquerChambre(e.target.value)} required>
                  <option value="">Sélectionner une chambre</option>
                  {/* Seules les chambres LIBRES sont proposées. Offrir une
                      chambre déjà prise revenait à faire remplir tout le
                      formulaire pour se voir refuser à l'envoi — l'information
                      était pourtant déjà à l'écran, dans la liste au-dessus. */}
                  {proposables.map(r => (
                    <option key={r.id} value={r.id}>{r.name || `Chambre (${r.surface}m²)`}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Date de début du bail</label>
                <input type="date" className="form-control" value={checkInStartDate} onChange={e => setCheckInStartDate(e.target.value)} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Loyer mensuel (€)</label>
                  <input type="number" step="0.01" className="form-control" value={checkInRent} onChange={e => setCheckInRent(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Provision charges (€)</label>
                  <input type="number" step="0.01" className="form-control" value={checkInCharges} onChange={e => setCheckInCharges(e.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Dépôt de garantie (€)</label>
                  <input type="number" step="0.01" className="form-control" value={checkInDeposit} onChange={e => setCheckInDeposit(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Frais d&apos;agence (€)</label>
                  <input type="number" step="0.01" className="form-control" value={checkInAgencyFees} onChange={e => setCheckInAgencyFees(e.target.value)} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-action btn-secondary" onClick={() => setCheckInUserId(null)}>Annuler</button>
                <button type="submit" className="btn-action btn-save" disabled={checkInLoading}>
                  {checkInLoading ? 'Enregistrement...' : 'Valider l\'entrée'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Notice modal */}
      {noticeId && (
        <div className="modal-overlay" onClick={() => setNoticeId(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Enregistrer un préavis</h3>
            {noticeError && <div className="tenants-error">{noticeError}</div>}
            <form onSubmit={handleGiveNotice} className="tenants-form">
              <div className="form-group">
                <label>Date de réception du préavis</label>
                <input type="date" className="form-control" value={noticeDate} onChange={e => setNoticeDate(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Date de sortie prévisionnelle</label>
                <input type="date" className="form-control" value={noticeExitDate} onChange={e => setNoticeExitDate(e.target.value)} required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-action btn-secondary" onClick={() => setNoticeId(null)}>Annuler</button>
                <button type="submit" className="btn-action btn-save" disabled={noticeLoading}>
                  {noticeLoading ? 'Enregistrement...' : 'Enregistrer le préavis'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modale état des lieux d'entrée */}
      {entryInvId && (
        <div className="modal-overlay" onClick={() => setEntryInvId(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>État des lieux d&apos;entrée</h3>
            {entryInvError && <div className="tenants-error">{entryInvError}</div>}
            <form onSubmit={handleEntryInventory} className="tenants-form">
              <div className="form-group">
                <label>Date de l&apos;état des lieux</label>
                <input
                  type="date"
                  className="form-control"
                  value={entryInvDate}
                  onChange={e => setEntryInvDate(e.target.value)}
                  required
                />
                {/* Cette date n'ouvre plus rien depuis le 2026-08-31 : l'accès
                    suit le début du bail. Elle reste une pièce du dossier, et
                    le dire évite qu'on la saisisse en espérant un effet. */}
                <small className="form-hint">
                  Cette date n&apos;ouvre pas l&apos;accès du locataire —
                  l&apos;espace s&apos;ouvre au début du bail. Elle complète son
                  dossier, où l&apos;état des lieux figure parmi les pièces
                  attendues.
                </small>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-action btn-secondary" onClick={() => setEntryInvId(null)}>
                  Annuler
                </button>
                <button type="submit" className="btn-action btn-save" disabled={entryInvLoading}>
                  {entryInvLoading ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {checkOutId && (
        <div className="modal-overlay" onClick={() => setCheckOutId(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Sortie du locataire</h3>
            {checkOutError && <div className="tenants-error">{checkOutError}</div>}
            <form onSubmit={handleCheckOut} className="tenants-form">
              <div className="form-group">
                <label>Date de fin du bail</label>
                <input type="date" className="form-control" value={checkOutDate} onChange={e => setCheckOutDate(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Date de l&apos;état des lieux de sortie</label>
                <input
                  type="date"
                  className="form-control"
                  value={checkOutInventoryDate}
                  onChange={e => setCheckOutInventoryDate(e.target.value)}
                  required
                />
                {/* C'est cette date qui pilote tout : la chambre se libère le
                    lendemain, et le locataire consulte encore son dossier
                    pendant six mois. La date de fin de bail, elle, est
                    contractuelle. */}
                <small className="form-hint">
                  La chambre sera proposée à la location dès le lendemain. Le
                  locataire gardera l&apos;accès à ses documents pendant 6 mois.
                </small>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-action btn-secondary" onClick={() => setCheckOutId(null)}>Annuler</button>
                <button type="submit" className="btn-action btn-danger" disabled={checkOutLoading}>
                  {checkOutLoading ? 'Enregistrement...' : 'Confirmer la sortie'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload document modal */}
      {uploadTenantId && (
        <div className="modal-overlay" onClick={() => setUploadTenantId(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Ajouter un document</h3>
            {uploadError && <div className="tenants-error">{uploadError}</div>}
            <form onSubmit={handleUpload} className="tenants-form">
              <div className="form-group">
                <label>Type de document</label>
                <select className="form-control" value={uploadDocType} onChange={e => setUploadDocType(e.target.value)}>
                  {DOCUMENT_TYPES.map(dt => (
                    <option key={dt} value={dt}>{DOCUMENT_TYPE_LABELS[dt]}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Fichier</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={e => setUploadFile(e.target.files?.[0] || null)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Notes (optionnel)</label>
                <input type="text" className="form-control" value={uploadNotes} onChange={e => setUploadNotes(e.target.value)} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-action btn-secondary" onClick={() => setUploadTenantId(null)}>Annuler</button>
                <button type="submit" className="btn-action btn-save" disabled={uploadLoading || !uploadFile}>
                  {uploadLoading ? 'Envoi...' : 'Envoyer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantDetailPage;
